import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router';
import { Spinner, CommonsInfo } from '@mabinso-registry/module-commons';
import AlertView from '../app/views/AlertView';
import auth from '../../utils/Auth';
import Globals from '../../utils/Globals';
import $ from 'jquery';
import './css/style.scss';

interface LoginFormData {
    username: string;
    password: string;
    remember: boolean;
}

interface LoginResponse {
    hasErrors: boolean;
    errors?: Array<{ message: string; messageKey: string }>;
    success?: Array<{ message: string; messageKey: string }>;
}

const Login: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = useParams();

    const [formData, setFormData] = useState<LoginFormData>({
        username: '',
        password: '',
        remember: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (token) {
            auth.setToken(token);
            navigate('/', { state: { from: location } });
        }
    }, [token, navigate, location]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        if (e) e.preventDefault();
        $('#username').validationEngine('hideAll');
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${Globals.baseUrl}login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: formData.username.trim(),
                    password: formData.password.trim(),
                    remember: formData.remember,
                }),
            });

            const token = response.headers.get('X-Authorisation');
            const data: LoginResponse = await response.json();

            if (!data.hasErrors && token) {
                if (data.success?.[0]) {
                    AlertView.show({
                        title: $._('alert.login.general'),
                        message: $._(data.success[0].message, data.success[0].messageKey),
                        okFunc: () => {},
                    });
                }

                auth.setToken(token, formData.remember);
                localStorage.setItem('isSaml', 'false');

                // Animate and redirect
                const loginElement = document.getElementById('login');
                if (loginElement) {
                    loginElement.style.transition = 'opacity 1.2s linear';
                    loginElement.style.opacity = '0';
                    setTimeout(() => {
                        const nextPath = location.state?.nextPathname || '/';
                        navigate(nextPath);
                    }, 1200);
                }
            } else {
                // setError($._((data.errors?.[0]?.message || 'Login failed'), data.errors?.[0]?.messageKey));
                $('#username').validationEngine('showPrompt', $._(data.errors[0].message, data.errors[0].messageKey), '', 'topRight');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Network error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div id="login" className="login-wrapper">
            <div id="hauptrahmen">
                <div className="loginscreen">
                    {isLoading && <Spinner mode="l" id="Login-Spinner" />}
                    <form onSubmit={handleSubmit}>
                        <fieldset>
                            <div className="input text">
                                <label>{$._('secure.username')}</label>
                                <input type="text" name="username" id="username" value={formData.username} onChange={handleInputChange} disabled={isLoading} />
                            </div>
                            <div className="input text">
                                <label>{$._('secure.password')}</label>
                                <input type="password" name="password" id="password" value={formData.password} onChange={handleInputChange} disabled={isLoading} />
                            </div>
                            <div className="input checkbox">
                                <div className="checkboxGroup">
                                    <div role="presentation" className={`option ${formData.remember ? 'active' : ''}`} onClick={() => setFormData((prev) => ({ ...prev, remember: !prev.remember }))}>
                                        <input className="remember" type="checkbox" name="remember" id="remember" checked={formData.remember} onChange={handleInputChange} disabled={isLoading} />
                                        <span className="optBeschriftung">{$._('secure.remember')}</span>
                                    </div>
                                </div>
                            </div>
                            {error && (
                                <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>
                                    {error}
                                </div>
                            )}
                        </fieldset>
                        <p className="version" style={{ color: '#01aad3', fontSize: '10px' }}>
                            Version: {Globals.serverConfig.version}&nbsp;
                            <Link to="/info">{$._('index.licence')}</Link>
                        </p>
                        <input type="submit" id="signin" disabled={isLoading} className="login" value={$._('secure.signin')} />
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;
